import React from 'react';
import { TableConfig } from '../../../../../../v2BaseComponents/RichTable/types';
import { APITypes } from '../../../../../../../primary-record-types';
import { getIntl } from '../../../../../../../utils/intl';
import { useRichTableState } from '../../../../../../v2BaseComponents/RichTable/useRichTableState';
import RichTable from '../../../../../../v2BaseComponents/RichTable';
import { useMaybeRenderedSideDrawerToggle } from '../../../../../../v2BaseComponents/SideDrawer/MaybeRenderedSideDrawer';
import { useProfilesTabNavigation } from '../../../../../../../utils/appNavigationNames';
import { useIsOrganizationAdmin } from '../../../../../../../hooks/api/useOrganization';
import _truncate from 'lodash/truncate';
import { usePromptButton } from '../../../../../../shared/PromptButton';
import Stack, { ContentStack } from '../../../../../../v2BaseComponents/Stack';
import InfoIcon from '../../../../../../v2BaseComponents/InfoIcon';
import { PromptActions } from '../../../../../../v2BaseComponents/Prompt';
import getName from '../../../../../../../utils/getName';
import useOrganizationMedicalProfiles from '../../../../../../../hooks/api/useOrganizationMedicalProfiles';
import getDateString from '../../../../../../../utils/getDateString';
import ClientProfileDetailsSideDrawerContent from './ClientProfileDetailsSideDrawerContent';
import { useIsMobileLayout } from '../../../../../../../hooks/useIsMobileLayout';
import Link from '../../../../../../v2BaseComponents/Link';
import { useOrganizationClientProfileUnreadInAppNotificationCount } from '../../../../../../../hooks/api/useOrganizationClientProfileUnreadInAppNotificationCount';
import NotificationCountIndicator from '../../../../../../v2BaseComponents/NotificationIndicator/NotificationCountIndicator';

const intl = getIntl('profiles-client-profiles-table', {
    profileDetails: 'Profile Details',
    edit: 'Edit',
    editClientProfile: 'Edit Client Profile',
    name: 'Name',
    dob: 'Date of Birth',
    view: 'View',
    sex: 'Sex',
    gender: 'Gender',
    delete: 'Remove Client',
    deleteProfileHeader: 'Remove Client?',
    deleteProfileContent: 'Are you sure you want to remove the client "{name}"?  This action cannot be undone.',
    deleteProfileConfirm: 'Delete Profile',
    cancel: 'Cancel',
});

export type ClientProfilesTableOwnProps = {};

export type ClientProfilesTableProps = ClientProfilesTableOwnProps;

const ClientProfilesTable: React.FC<ClientProfilesTableProps> = (_props) => {
    const { getUnreadInAppNotificationCountForClientMedicalProfile } =
        useOrganizationClientProfileUnreadInAppNotificationCount({});
    const isMobileLayout = useIsMobileLayout();
    const profilesTabNavigator = useProfilesTabNavigation();
    const [tableState, setTableState] = useRichTableState();
    const {
        organizationMedicalProfiles,
        isFetchingOrganizationMedicalProfiles,
        deleteOrganizationMedicalProfile,
        isDeletingOrganizationMedicalProfile,
    } = useOrganizationMedicalProfiles({});
    const getNameDisplay = (clientProfile: APITypes.organizationService.OrganizationMedicalProfileDetails) =>
        getName(clientProfile.contactDetails);
    const getDOBDisplay = (clientProfile: APITypes.organizationService.OrganizationMedicalProfileDetails) =>
        getDateString(clientProfile.contactDetails.dob, 'date');
    const getDOBSortValue = (clientProfile: APITypes.organizationService.OrganizationMedicalProfileDetails) =>
        clientProfile.contactDetails.dob ? new Date(clientProfile.contactDetails.dob) : null;
    const getSex = (clientProfile: APITypes.organizationService.OrganizationMedicalProfileDetails) =>
        clientProfile.contactDetails.sex || '';
    const getGender = (clientProfile: APITypes.organizationService.OrganizationMedicalProfileDetails) =>
        clientProfile.contactDetails.gender || '';

    const clientProfileDetailsSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'clientProfileDetails',
        title: intl('profileDetails'),
        renderContent: () => (
            <ClientProfileDetailsSideDrawerContent clientMedicalProfileId={tableState.selectedRowKeys[0]} />
        ),
        mobileOnFocus: () =>
            profilesTabNavigator.navigate('client-profile', {
                profileId: tableState.selectedRowKeys[0],
            }),
    });
    const isOrganizationAdmin = useIsOrganizationAdmin();
    const selectedProfiles = tableState.selectedRowKeys
        .map((key) => organizationMedicalProfiles.find((profile) => profile.medicalProfileId === key))
        .filter(Boolean);

    const deleteButtonModal = usePromptButton({
        renderPromptContents: (onClose) => (
            <ContentStack>
                <InfoIcon
                    alertIconProps={{
                        name: 'AlertTriangle',
                        variant: 'error',
                    }}
                    title={intl('deleteProfileHeader')}
                    subText={intl('deleteProfileContent', {
                        name: selectedProfiles[0]?.contactDetails ? getName(selectedProfiles[0]?.contactDetails) : '',
                    })}
                />
                <PromptActions
                    actions={[
                        {
                            title: intl('deleteProfileConfirm'),
                            onPress: () => {
                                deleteOrganizationMedicalProfile(tableState.selectedRowKeys[0]);
                                onClose();
                            },
                            variant: 'alert',
                            icon: 'Trash2',
                            testID: 'delete-client-profile-confirm',
                        },
                        {
                            title: intl('cancel'),
                            onPress: onClose,
                            variant: 'secondary',
                            icon: 'X',
                            testID: 'delete-client-profile-cancel',
                        },
                    ]}
                />
            </ContentStack>
        ),
        promptTitle: intl('deleteProfileHeader'),
    });

    const clientProfilesTableConfig: TableConfig<APITypes.organizationService.OrganizationMedicalProfileDetails> = {
        columns: [
            {
                label: intl('name'),
                columnKey: 'name',
                getSortValue: getNameDisplay,
                getContentKey: getNameDisplay,
                getSearchValue: getNameDisplay,
                render: (item) => {
                    const name = getName(item.contactDetails);
                    const notificationCount = getUnreadInAppNotificationCountForClientMedicalProfile(
                        item.medicalProfileId,
                    );
                    return (
                        <Stack horizontal space={4} align="center">
                            <Link
                                onPress={() => {
                                    if (isMobileLayout) {
                                        profilesTabNavigator.navigate('profile', {
                                            profileId: item.medicalProfileId,
                                        });
                                    } else {
                                        profilesTabNavigator.navigate('profile-activity', {
                                            profileId: item.medicalProfileId,
                                        });
                                    }
                                }}
                                linkColor="blue"
                                variant="table-content"
                                noUnderline
                                testID="client-record-link"
                            >
                                {name}
                            </Link>
                            {notificationCount ? <NotificationCountIndicator count={notificationCount} /> : null}
                        </Stack>
                    );
                },
            },
            {
                label: intl('dob'),
                columnKey: 'dob',
                getSortValue: getDOBSortValue,
                getContentKey: getDOBDisplay,
                getSearchValue: getDOBDisplay,
                render: getDOBDisplay,
                hideOnMobile: true,
            },
            {
                label: intl('sex'),
                columnKey: 'sex',
                getSortValue: getSex,
                getContentKey: getSex,
                getSearchValue: getSex,
                getDiscreetValue: getSex,
                render: getSex,
                hideOnMobile: true,
            },
            {
                label: intl('gender'),
                columnKey: 'gender',
                getSortValue: getGender,
                getContentKey: getGender,
                getSearchValue: getGender,
                getDiscreetValue: getGender,
                render: getGender,
                hideOnMobile: true,
            },
        ],
        getRowKey: (item) => item.medicalProfileId,
    };

    return (
        <>
            <RichTable
                config={clientProfilesTableConfig}
                state={tableState}
                onStateChange={setTableState}
                data={organizationMedicalProfiles}
                minWidth={'100%'}
                allowRowSelection
                style={{ height: '100%' }}
                isFetching={isFetchingOrganizationMedicalProfiles}
                actions={[
                    {
                        title: intl('profileDetails'),
                        icon: 'Info',
                        onPress: () => {
                            clientProfileDetailsSideDrawer.onOpen();
                        },
                        shouldShow: (selected) => selected.length === 1,
                        testID: 'client-profiles-details',
                    },
                    {
                        title: intl('delete'),
                        icon: 'Trash2',
                        onPress: deleteButtonModal.onPress,
                        shouldShow: (selected) => isOrganizationAdmin && selected.length === 1,
                        shouldDisable: () => isDeletingOrganizationMedicalProfile,
                        testID: 'delete-client-profile',
                    },
                ]}
            />
            {clientProfileDetailsSideDrawer.SideDrawer}
            {deleteButtonModal.Prompt}
        </>
    );
};

export default React.memo(ClientProfilesTable);
